import { googleLogout } from '@react-oauth/google';
import { api, resetAll } from 'app/api';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import MyPlanIcon from 'assets/icons/my-plan-icon.svg';
import SettingsIcon from 'assets/icons/settings-icon.svg';
import routes from 'constants/routes';
import { CUSTOM_SUBSCRIPTION_PLANS } from 'constants/subscription';
import { Bug } from 'lucide-react';
import { TEST_IDS } from 'mocks/ids';
import { useLogoutMutation } from 'modules/auth/authApi';
import { useIsAuthentiseUser } from 'modules/auth/useAuth';
import { logout, selectUserInfo } from 'modules/auth/userSlice';
import { copilotApi } from 'modules/copilot/copilotApi';
import { useModal } from 'modules/modals/ModalProvider';
import { notificationApi } from 'modules/notification/notificationApi';
import { useLazyGetStripeDashboardLinkQuery } from 'modules/subscriptionsPlans/subscriptionsPlansApi';
import { selectSubscriptionName } from 'modules/subscriptionsPlans/subscriptionsPlansSlice';
import { userApi } from 'modules/user/userApi';
import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import colors from 'theme/colors';
import { Avatar, DropDownAction, Icon } from 'ui';
import { getDebugInfo } from 'utils/debug';
import { isAdmin } from 'utils/helpers';
import { SupportAction } from 'hooks/useSupportActions';

interface IProfileMenuProps {
  isMobile: boolean;
  canUpgrade: boolean;
  supportActions: SupportAction[];
}

const ProfileMenu: React.FC<IProfileMenuProps> = ({ isMobile, canUpgrade, supportActions }) => {
  const currentSubscriptionName = useAppSelector(selectSubscriptionName);
  const { user, isAuthentiseUser } = useIsAuthentiseUser();

  const { open } = useModal();

  const integrationsItemRef = useRef<HTMLDivElement>(null);

  const { avatar, userRole } = useAppSelector(selectUserInfo);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onProfile = () => {
    navigate(routes.account);
  };

  // Calling the /logout/ endpoint will allow the httpOnly session cookie to be destroyed at the backend.
  const [requestLogoutEndpoint] = useLogoutMutation();

  const [fetchDashBoardUrl] = useLazyGetStripeDashboardLinkQuery();

  const onLogout = () => {
    googleLogout();
    dispatch(logout());
    dispatch(resetAll());

    requestLogoutEndpoint(null).then(() =>
      dispatch(userApi.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true }))
    );
    dispatch(api.util.resetApiState());
    dispatch(userApi.util.resetApiState());
    dispatch(notificationApi.util.resetApiState());
    dispatch(copilotApi.util.resetApiState());

    // Reset document title, because we changing it when user has some unread notifications
    document.title = 'Threads';
    toast.success('Successfully logged out.');
  };

  const onIntegrations = () => {
    open({
      variant: 'center',
      contentLabel: 'integrations',
      name: 'Enable Google integration',
      modal: 'choseIntegration',
      id: 'modal-choose-integration',
    });
  };

  const isCurrentSubscriptionFree = useMemo(
    () =>
      currentSubscriptionName &&
      currentSubscriptionName.toLocaleLowerCase() ===
        CUSTOM_SUBSCRIPTION_PLANS.Free.toLocaleLowerCase(),
    [currentSubscriptionName]
  );

  const handleMyPlanClick = async () => {
    if (isCurrentSubscriptionFree) {
      navigate(`${routes.subscriptionsPlans}/dashboard`);
      return;
    }
    fetchDashBoardUrl().then((res) => {
      window.open(res.data?.redirectUrl as string, '_self', 'noopener,noreferrer');
    });
  };

  const handleDebugClick = async () => {
    const text = getDebugInfo(user?.email, true).reduce((acc, curr) => {
      return `${acc}${curr.name}: ${curr.value}\n`;
    }, '');
    await navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const actions = useMemo(() => {
    const settings = {
      id: 'workspace-settings',
      title: 'Settings',
      icon: <img alt="Settings Icon" width={24} height={24} src={SettingsIcon} />,
      action: () => navigate(routes.members),
      isShown: true,
    };

    const plan = {
      id: 'subscriptionPortal',
      title: 'My Plan',
      icon: <img alt="Subscription Icon" width={24} height={24} src={MyPlanIcon} />,
      action: handleMyPlanClick,
      isShown: true,
    };

    const debug = {
      id: 'debug',
      title: 'Debug Info',
      icon: <Bug />,
      action: handleDebugClick,
      isShown: true,
    };

    return [
      {
        id: 'edit',
        title: 'Profile',
        icon: <Icon icon="UserIcon" path={colors.white} />,
        action: onProfile,
        isShown: true, // Always shown on both desktop & mobile
      },
      {
        id: 'integrations',
        title: 'Integrations',
        icon: <Icon icon="IntegrationsIcon" path={colors.white} />,
        ref: integrationsItemRef,
        action: onIntegrations,
        isShown: true, // Always shown on both desktop & mobile
      },
      ...(canUpgrade ? [plan] : []),
      ...(isAdmin(userRole) ? [settings] : []),
      ...(isAuthentiseUser ? [debug] : []),
      ...(isMobile ? supportActions : []),
      {
        id: 'delete',
        title: 'Logout',
        icon: <Icon icon="ExitIcon" path={colors.white} />,
        action: onLogout,
        isShown: true, // Always shown on both desktop & mobile
        topDivider: isMobile,
      },
    ];
  }, [canUpgrade, userRole, isMobile, supportActions]);

  return (
    <DropDownAction actions={actions} cypressAttribute="header-profile-dropdown">
      <Avatar data-testid={TEST_IDS.profile_menu} src={avatar} style={{ cursor: 'pointer' }} />
    </DropDownAction>
  );
};

export default ProfileMenu;
